import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Search, Phone, MessageCircle, MapPin, Clock, Star,
  Package, ChevronDown, ChevronUp, X, Globe
} from 'lucide-react';
import { isColorDark, formatPrice as utilFormatPrice } from './public/utils';
import ProductCard from './public/components/ProductCard';
import BannerSlider from './public/components/BannerSlider';
import WebsiteSectionRenderer from './public/components/WebsiteSectionRenderer';
import GoogleReviewBubble from './public/components/GoogleReviewBubble';

const getSocialDeeplink = (platform, handle) => {
  if (!handle) return null;
  const clean = handle.replace(/^@/, '').replace(/\s/g, '').trim();
  switch (platform) {
    case 'instagram':
      if (handle.startsWith('http')) return handle;
      return `https://www.instagram.com/${clean}`;
    case 'facebook':
      if (handle.startsWith('http')) return handle;
      return `https://www.facebook.com/${clean}`;
    case 'tiktok':
      if (handle.startsWith('http')) return handle;
      return `https://www.tiktok.com/@${clean}`;
    case 'website':
      if (handle.startsWith('http')) return handle;
      return `https://${handle}`;
    default:
      return null;
  }
};

const InstagramSvg = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const FacebookSvg = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const TikTokSvg = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V9.24a8.16 8.16 0 0 0 4.76 1.51v-3.45a4.85 4.85 0 0 1-1-.61z" />
  </svg>
);

const SocialBar = ({ shop, accentColor, cardBg, isDark }) => {
  const socials = [
    { key: 'instagram', value: shop.instagram, icon: InstagramSvg, label: 'Instagram' },
    { key: 'facebook', value: shop.facebook, icon: FacebookSvg, label: 'Facebook' },
    { key: 'tiktok', value: shop.tiktok, icon: TikTokSvg, label: 'TikTok' },
    { key: 'website', value: shop.website, icon: Globe, label: 'Site web' },
  ].filter(s => s.value);

  if (socials.length === 0) return null;

  return (
    <div style={{ backgroundColor: cardBg, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
      <div className="max-w-2xl mx-auto px-4 py-2.5">
        <div className="flex items-center gap-2">
          {socials.map(({ key, value, icon: Icon, label }) => {
            const url = getSocialDeeplink(key, value);
            return (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-105 active:scale-95"
                style={{
                  backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                  color: accentColor
                }}
                data-testid={`social-link-${key}`}
              >
                <Icon size={15} color={accentColor} />
                <span>{label}</span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const LegalFooter = ({ slug, legalPages, isDark, cardBg }) => {
  const links = [];
  if (legalPages.mentions_legales) links.push({ key: 'mentions_legales', label: 'Mentions legales' });
  if (legalPages.politique_confidentialite) links.push({ key: 'politique_confidentialite', label: 'Politique de confidentialite' });
  if (legalPages.cgv) links.push({ key: 'cgv', label: 'CGV' });
  if (links.length === 0) return null;
  return (
    <footer className="pb-24 pt-8" data-testid="legal-footer">
      <div className="max-w-2xl mx-auto px-4">
        <div className="border-t pt-4 flex flex-wrap justify-center gap-4" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
          {links.map(({ key, label }) => (
            <a
              key={key}
              href={`/s/${slug}/legal/${key}`}
              className="text-xs transition-opacity hover:opacity-100"
              style={{ opacity: 0.5 }}
              data-testid={`footer-link-${key}`}
            >
              {label}
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicCatalog = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  const fetchCatalog = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/shops/${slug}`);
      setData(response.data);
      const expanded = {};
      response.data.categories.forEach(cat => { expanded[cat.id] = true; });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Catalogue non trouvé');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const trackEvent = async (type, productId = null) => {
    try {
      await axios.post(`${API}/public/shops/${slug}/events`, { type, productId });
    } catch (e) {}
  };

  const handleWhatsApp = (product = null) => {
    trackEvent('WHATSAPP_CLICK', product?.id);
    const phone = data.shop.whatsappNumber?.replace(/\s/g, '');
    const message = product 
      ? `Bonjour, je souhaite le produit "${product.name}". Est-il disponible ?`
      : `Bonjour, je souhaite des informations sur votre catalogue.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCall = () => {
    trackEvent('CALL_CLICK');
    window.location.href = `tel:${data.shop.phone}`;
  };

  const formatPrice = (cents) => {
    if (!cents) return null;
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  };

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-zinc-800 border-t-amber-500 animate-spin" />
          <p className="text-zinc-500 animate-pulse">Chargement du catalogue...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-zinc-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <Package className="w-12 h-12 text-zinc-600" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Catalogue introuvable</h1>
          <p className="text-zinc-500 mb-6">Ce catalogue n'existe pas ou n'est plus disponible.</p>
          <Button onClick={() => navigate('/')} className="rounded-full bg-amber-500 hover:bg-amber-600 text-black">
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  const { shop, categories, products, featuredProducts } = data;
  const banners = data.banners || [];
  const legalPages = data.legalPages || {};
  const accentColor = shop.accentColor || '#F59E0B';
  const headerColor = shop.headerColor || accentColor;
  const pageBg = shop.backgroundColor || '#09090B';
  const cardBg = shop.cardColor || '#18181B';
  const textColor = shop.textColor || '#FAFAFA';
  const isDark = isColorDark(pageBg);
  const homepageSections = data.homepageSections || [];
  const isWebsiteMode = shop.displayMode === 'website' && homepageSections.length > 0;

  const theme = { pageBg, cardBg, textColor, accentColor, isDark, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', mutedText: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' };

  // ===== WEBSITE MODE =====
  if (isWebsiteMode) {
    // Find categories NOT shown on homepage sections
    const homepageCatIds = new Set();
    homepageSections.forEach(s => {
      if (s.type === 'categories' && s.content?.categoryIds) {
        s.content.categoryIds.forEach(id => homepageCatIds.add(id));
      }
    });
    const navCategories = categories;

    return (
      <div className="min-h-screen" style={{ backgroundColor: pageBg, color: textColor }} data-testid="public-catalog-website">
        {/* Header */}
        <header style={{ backgroundColor: headerColor }}>
          <div className="max-w-2xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {shop.logoUrl ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${shop.logoUrl}`} alt={shop.name} className="w-14 h-14 rounded-xl object-cover border-2 border-black/20" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-black/20 flex items-center justify-center text-2xl font-bold" style={{ color: isColorDark(headerColor) ? '#fff' : '#000' }}>
                    {shop.name.charAt(0)}
                  </div>
                )}
                <h1 className="text-xl font-bold uppercase tracking-wide" style={{ color: isColorDark(headerColor) ? '#fff' : '#000' }}>{shop.name}</h1>
              </div>
              {shop.address && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shop.address)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs sm:text-sm opacity-80 hover:opacity-100 hover:underline transition-opacity max-w-[140px] sm:max-w-[250px]" style={{ color: isColorDark(headerColor) ? '#fff' : '#000' }} data-testid="address-maps-link">
                  <MapPin className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{shop.address}</span>
                </a>
              )}
            </div>
          </div>
        </header>

        <SocialBar shop={shop} accentColor={accentColor} cardBg={cardBg} isDark={isDark} />

        {/* Horizontal category nav bar */}
        {navCategories.length > 0 && (
          <div style={{ backgroundColor: cardBg, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
            <div className="max-w-2xl mx-auto px-4 py-3">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                {navCategories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => navigate(`/s/${slug}/c/${cat.id}`)}
                    className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all"
                    style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', color: textColor }}
                    data-testid={`ws-nav-cat-${cat.id}`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Homepage sections */}
        <main className="max-w-2xl mx-auto px-4 py-6 pb-32">
          {homepageSections.map(section => (
            <WebsiteSectionRenderer
              key={section.id}
              section={section}
              allProducts={products}
              allCategories={categories}
              slug={slug}
              theme={theme}
              formatPrice={formatPrice}
              shopName={shop.name}
            />
          ))}
          <LegalFooter slug={slug} legalPages={legalPages} isDark={isDark} cardBg={cardBg} />
        </main>

        {/* Bottom bar */}
        <div className="fixed bottom-0 left-0 right-0 backdrop-blur-lg p-4 z-40" style={{ backgroundColor: `${cardBg}ee`, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
          <div className="max-w-2xl mx-auto flex gap-3">
            {shop.whatsappNumber && (
              <Button className="flex-1 h-14 rounded-xl gap-2 text-base font-semibold" style={{ backgroundColor: '#25D366' }} onClick={() => handleWhatsApp()} data-testid="ws-whatsapp-btn">
                <MessageCircle className="w-5 h-5" /> Contacter sur WhatsApp
              </Button>
            )}
            {shop.phone && shop.whatsappNumber && (
              <Button variant="outline" className="h-14 w-14 rounded-xl shrink-0" style={{ borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)', color: textColor }} onClick={handleCall} data-testid="ws-call-btn">
                <Phone className="w-5 h-5" />
              </Button>
            )}
            {shop.phone && !shop.whatsappNumber && (
              <Button className="flex-1 h-14 rounded-xl gap-2 text-base font-semibold text-black" style={{ backgroundColor: accentColor }} onClick={handleCall}>
                <Phone className="w-5 h-5" /> Appeler
              </Button>
            )}
          </div>
        </div>

        <GoogleReviewBubble googleReviewUrl={shop.googleReviewUrl} accentColor={accentColor} shopSlug={slug} />
      </div>
    );
  }

  // ===== CATALOG MODE (existing) =====

  const filteredProducts = searchTerm 
    ? products.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : products;

  const productsByCategory = categories.reduce((acc, cat) => {
    acc[cat.id] = filteredProducts.filter(p => p.categoryId === cat.id);
    return acc;
  }, {});

  const getCategoryCount = (catId) => productsByCategory[catId]?.length || 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: pageBg, color: textColor }} data-testid="public-catalog">
      <header style={{ backgroundColor: headerColor }}>
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {shop.logoUrl ? (
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}${shop.logoUrl}`}
                  alt={shop.name}
                  className="w-14 h-14 rounded-xl object-cover border-2 border-black/20"
                />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-black/20 flex items-center justify-center text-2xl font-bold" style={{ color: isDark ? '#fff' : '#000' }}>
                  {shop.name.charAt(0)}
                </div>
              )}
              <h1 className="text-xl font-bold uppercase tracking-wide" style={{ color: isColorDark(headerColor) ? '#fff' : '#000' }}>{shop.name}</h1>
            </div>
            {shop.hoursText && (
              <button className="flex items-center gap-2 bg-black/20 px-4 py-2 rounded-full text-sm font-medium" style={{ color: isColorDark(headerColor) ? '#fff' : '#000' }}>
                <Clock className="w-4 h-4" />
                Horaires
              </button>
            )}
          </div>
        </div>
      </header>

      <div style={{ backgroundColor: cardBg, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
        <div className="max-w-2xl mx-auto px-4 py-3">
          {shop.hoursText && (
            <div className="flex items-center gap-2 text-sm mb-2">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span className="text-green-500 font-medium">Ouvert</span>
              <span style={{ opacity: 0.6 }}>{shop.hoursText}</span>
            </div>
          )}
          <div className="flex items-center gap-4 text-sm" style={{ opacity: 0.6 }}>
            {shop.phone && (
              <button onClick={handleCall} className="flex items-center gap-1 hover:opacity-100 transition-opacity">
                <Phone className="w-4 h-4" />
                {shop.phone}
              </button>
            )}
            {shop.address && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shop.address)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:underline cursor-pointer" data-testid="address-maps-link">
                <MapPin className="w-4 h-4" />
                <span className="truncate max-w-[200px]">{shop.address}</span>
              </a>
            )}
          </div>
        </div>
      </div>

      <SocialBar shop={shop} accentColor={accentColor} cardBg={cardBg} isDark={isDark} />

      <div style={{ backgroundColor: cardBg, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => {
                  const el = document.getElementById(`category-${cat.id}`);
                  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all"
                style={{ backgroundColor: accentColor, color: 'black' }}
              >
                {cat.name} ({getCategoryCount(cat.id)})
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-6 pb-32">
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ opacity: 0.4 }} />
          <Input
            placeholder="Rechercher un produit..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 h-12 rounded-xl border"
            style={{ backgroundColor: cardBg, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', color: textColor }}
            data-testid="catalog-search"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-4 top-1/2 -translate-y-1/2" style={{ opacity: 0.5 }}>
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {!searchTerm && banners.length > 0 && (
          <BannerSlider banners={banners} isDark={isDark} />
        )}

        {!searchTerm && featuredProducts.length > 0 && (
          <section id="featured-section" className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5" style={{ color: accentColor }} />
                <h2 className="text-lg font-bold">Produits vedettes</h2>
              </div>
              <Badge variant="secondary" className="bg-zinc-800 text-zinc-300">
                {featuredProducts.length}
              </Badge>
            </div>
            <div className="space-y-3">
              {featuredProducts.map(product => (
                <ProductCard 
                  key={product.id}
                  product={product}
                  slug={slug}
                  accentColor={accentColor}
                  cardBg={cardBg}
                  isDark={isDark}
                  formatPrice={formatPrice}
                  onWhatsApp={() => handleWhatsApp(product)}
                  shopName={shop.name}
                />
              ))}
            </div>
          </section>
        )}

        {categories.map(cat => {
          const catProducts = productsByCategory[cat.id] || [];
          if (catProducts.length === 0) return null;
          
          const isExpanded = expandedCategories[cat.id];
          
          return (
            <section key={cat.id} id={`category-${cat.id}`} className="mb-6">
              <button
                onClick={() => toggleCategory(cat.id)}
                className="w-full flex items-center justify-between py-3"
                style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}
              >
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold">{cat.name}</h2>
                  <Badge variant="secondary" className="rounded-full" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)' }}>
                    {catProducts.length}
                  </Badge>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5" style={{ opacity: 0.4 }} />
                ) : (
                  <ChevronDown className="w-5 h-5" style={{ opacity: 0.4 }} />
                )}
              </button>

              {isExpanded && (
                <div className="space-y-3 mt-4">
                  {catProducts.map(product => (
                    <ProductCard 
                      key={product.id}
                      product={product}
                      slug={slug}
                      accentColor={accentColor}
                      cardBg={cardBg}
                      isDark={isDark}
                      formatPrice={formatPrice}
                      onWhatsApp={() => handleWhatsApp(product)}
                      shopName={shop.name}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}

        {searchTerm && filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">Aucun résultat pour "{searchTerm}"</p>
          </div>
        )}
        <LegalFooter slug={slug} legalPages={legalPages} isDark={isDark} cardBg={cardBg} />
      </main>

      <div className="fixed bottom-0 left-0 right-0 backdrop-blur-lg p-4 z-40" style={{ backgroundColor: `${cardBg}ee`, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
        <div className="max-w-2xl mx-auto flex gap-3">
          {shop.whatsappNumber && (
            <Button
              className="flex-1 h-14 rounded-xl gap-2 text-base font-semibold"
              style={{ backgroundColor: '#25D366' }}
              onClick={() => handleWhatsApp()}
              data-testid="whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Contacter sur WhatsApp
            </Button>
          )}
          {shop.phone && !shop.whatsappNumber && (
            <Button
              className="flex-1 h-14 rounded-xl gap-2 text-base font-semibold text-black"
              style={{ backgroundColor: accentColor }}
              onClick={handleCall}
              data-testid="call-btn"
            >
              <Phone className="w-5 h-5" />
              Appeler
            </Button>
          )}
          {shop.phone && shop.whatsappNumber && (
            <Button
              variant="outline"
              className="h-14 w-14 rounded-xl shrink-0"
              style={{ borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)', color: textColor }}
              onClick={handleCall}
              data-testid="call-btn"
            >
              <Phone className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      <GoogleReviewBubble googleReviewUrl={shop.googleReviewUrl} accentColor={accentColor} />
    </div>
  );
};

export default PublicCatalog;
